import type { BillingFrequency } from '../types/agreement'

export interface BillingPeriod {
  start: string
  end: string
  label: string
}

function parseDate(s: string): Date | null {
  const parts = (s || '').split('-').map(Number)
  if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) return null
  return new Date(parts[0], parts[1] - 1, parts[2])
}

function fmt(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

function addMonths(d: Date, n: number): Date {
  const r = new Date(d.getFullYear(), d.getMonth() + n, 1)
  return r
}

function addDays(d: Date, n: number): Date {
  const r = new Date(d)
  r.setDate(r.getDate() + n)
  return r
}

function daysInMonth(year: number, monthIndex: number): number {
  return new Date(year, monthIndex + 1, 0).getDate()
}

/** Normalize 1–31; 31 (or any day past month length) = last day of that month. */
export function clampBillingDay(year: number, monthIndex: number, dayOfMonth: number): number {
  const dim = daysInMonth(year, monthIndex)
  if (dayOfMonth >= 31) return dim
  return Math.min(Math.max(1, dayOfMonth), dim)
}

export function dateWithBillingDay(year: number, monthIndex: number, dayOfMonth: number): Date {
  const day = clampBillingDay(year, monthIndex, dayOfMonth)
  return new Date(year, monthIndex, day)
}

/** First bill day on or after `start`. */
export function firstBillingOnOrAfter(start: Date, dayOfMonth: number): Date {
  let candidate = dateWithBillingDay(start.getFullYear(), start.getMonth(), dayOfMonth)
  if (candidate < start) {
    const nextMonth = addMonths(start, 1)
    candidate = dateWithBillingDay(nextMonth.getFullYear(), nextMonth.getMonth(), dayOfMonth)
  }
  return candidate
}

export function nextBillingDate(from: Date, dayOfMonth: number, monthStep: number): Date {
  const base = addMonths(from, monthStep)
  return dateWithBillingDay(base.getFullYear(), base.getMonth(), dayOfMonth)
}

/**
 * Generate billing periods for a contract date range and frequency.
 *
 * Advance billing: a period is included once its start date is on or before today
 * (e.g. July 1–31 appears starting July 1, covering that month forward).
 *
 * For monthly/quarterly/annually, `billingDayOfMonth` (default 1) anchors period starts.
 * Day 31 = last day of each month.
 */
export function generateBillingPeriods(
  startDateStr: string,
  endDateStr: string | null | undefined,
  frequency: BillingFrequency | string,
  billingDayOfMonth: number = 1
): BillingPeriod[] {
  const start = parseDate(startDateStr)
  if (!start) return []
  const contractEnd = endDateStr ? parseDate(endDateStr) : null
  const today = new Date()
  today.setHours(0, 0, 0, 0)

  const day = Number.isFinite(billingDayOfMonth) && billingDayOfMonth >= 1 ? billingDayOfMonth : 1
  const periods: BillingPeriod[] = []

  const pushAdvance = (pStart: Date, pEnd: Date): boolean => {
    if (pStart > today) return false
    if (contractEnd && pStart > contractEnd) return false
    let end = pEnd
    if (contractEnd && end > contractEnd) end = contractEnd
    if (end < pStart) return false
    periods.push({
      start: fmt(pStart),
      end: fmt(end),
      label: `${fmt(pStart)} – ${fmt(end)}`,
    })
    return true
  }

  switch (frequency) {
    case 'weekly': {
      let cursor = new Date(start)
      while (cursor <= today) {
        if (contractEnd && cursor > contractEnd) break
        const pEnd = addDays(cursor, 6)
        if (!pushAdvance(cursor, pEnd)) break
        cursor = addDays(pEnd, 1)
      }
      break
    }
    case 'bi_weekly': {
      let cursor = new Date(start)
      while (cursor <= today) {
        if (contractEnd && cursor > contractEnd) break
        const pEnd = addDays(cursor, 13)
        if (!pushAdvance(cursor, pEnd)) break
        cursor = addDays(pEnd, 1)
      }
      break
    }
    case 'quarterly': {
      let cursor = firstBillingOnOrAfter(start, day)
      while (cursor <= today) {
        if (contractEnd && cursor > contractEnd) break
        const nextStart = nextBillingDate(cursor, day, 3)
        const pEnd = addDays(nextStart, -1)
        if (!pushAdvance(cursor, pEnd)) break
        cursor = nextStart
      }
      break
    }
    case 'annually': {
      let cursor = firstBillingOnOrAfter(start, day)
      while (cursor <= today) {
        if (contractEnd && cursor > contractEnd) break
        const nextStart = nextBillingDate(cursor, day, 12)
        const pEnd = addDays(nextStart, -1)
        if (!pushAdvance(cursor, pEnd)) break
        cursor = nextStart
      }
      break
    }
    case 'monthly':
    default: {
      let cursor = firstBillingOnOrAfter(start, day)
      while (cursor <= today) {
        if (contractEnd && cursor > contractEnd) break
        const nextStart = nextBillingDate(cursor, day, 1)
        const pEnd = addDays(nextStart, -1)
        if (!pushAdvance(cursor, pEnd)) break
        cursor = nextStart
      }
      break
    }
  }

  return periods
}

/** True when the period should appear in Ready to Bill (advance: on/after period start). */
export function isBillingPeriodReady(periodStart: string, asOf: Date = new Date()): boolean {
  const start = parseDate(periodStart)
  if (!start) return false
  const d = new Date(asOf)
  d.setHours(0, 0, 0, 0)
  return start <= d
}

export function periodAmount(
  billingMethod: string,
  contractTotal: number,
  perPeriodAmount: number,
  periodCount: number
): number {
  if (billingMethod === 'divide_total' && periodCount > 0) {
    return contractTotal / periodCount
  }
  return perPeriodAmount
}

export function showBillingDayPicker(frequency: string | null | undefined): boolean {
  return frequency === 'monthly' || frequency === 'quarterly' || frequency === 'annually'
}
